import React, { useState } from 'react';
import {
  X,
  ChevronDown,
  Archive,
  ArchiveRestore,
  Ban,
  RotateCcw,
  Loader2,
  UserPlus,
} from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@roger/ui-components';
import { updateIntake } from '../services/api';
import { useToast } from '../hooks/useToastContext';
import { IntakeUserAssignmentDialog } from './IntakeUserAssignmentDialog';
import { IntakeRecord } from '@/store/slices/intakeSlice';

type BulkActionType = 'archive' | 'unarchive' | 'non_admit' | 'undo_non_admit';

interface IntakeSelectionActionsProps {
  selectedIntakes: IntakeRecord[];
  onClearSelection: () => void;
  /** If provided, used on partial failure to remove only succeeded IDs from selection so retry only affects failed intakes. */
  onRemoveFromSelection?: (ids: string[]) => void;
  onAssignComplete?: () => void;
}

const IntakeSelectionActions: React.FC<IntakeSelectionActionsProps> = ({
  selectedIntakes,
  onClearSelection,
  onRemoveFromSelection,
  onAssignComplete,
}) => {
  const selectedIntakeIds = selectedIntakes.map(intake => intake.id);
  // Only show actions that apply to at least one selected intake
  const canArchive = selectedIntakes.some(intake => !intake.archived);
  const canUnarchive = selectedIntakes.some(intake => !!intake.archived);
  const canNonAdmit = selectedIntakes.some(intake => !intake.non_admit);
  const canUndoNonAdmit = selectedIntakes.some(intake => !!intake.non_admit);
  const { error: showError } = useToast();
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<BulkActionType | null>(
    null
  );
  const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);

  const handleBulkActionClick = (type: BulkActionType) => {
    setBulkActionType(type);
    setTimeout(() => setActionDialogOpen(true), 100);
  };

  const handleBulkActionConfirm = async () => {
    if (!bulkActionType || selectedIntakeIds.length === 0) return;
    setIsBulkActionLoading(true);
    const verb =
      bulkActionType === 'archive'
        ? 'archive'
        : bulkActionType === 'unarchive'
          ? 'unarchive'
          : bulkActionType === 'non_admit'
            ? 'mark non-admit'
            : 'undo non-admit';
    const body =
      bulkActionType === 'archive'
        ? { archived: true }
        : bulkActionType === 'unarchive'
          ? { archived: false }
          : bulkActionType === 'non_admit'
            ? { non_admit: true }
            : { non_admit: false };
    const results = await Promise.allSettled(
      selectedIntakeIds.map(intakeId =>
        updateIntake({
          path: { intake_id: intakeId },
          body,
        })
      )
    );
    const succeeded = selectedIntakeIds.filter(
      (_, i) => results[i].status === 'fulfilled'
    );
    const failedCount = results.filter(r => r.status === 'rejected').length;
    if (failedCount === 0) {
      setActionDialogOpen(false);
      setBulkActionType(null);
      onClearSelection();
      onAssignComplete?.();
    } else {
      if (succeeded.length > 0 && onRemoveFromSelection) {
        onRemoveFromSelection(succeeded);
      }
      showError(
        `Failed to ${verb} ${failedCount === selectedIntakeIds.length ? 'intakes' : `${failedCount} of ${selectedIntakeIds.length} intakes`}`,
        succeeded.length > 0
          ? `${succeeded.length} succeeded. You can retry for the remaining.`
          : 'Please try again or refresh the page.'
      );
    }
    setIsBulkActionLoading(false);
  };

  const handleAssignClick = () => {
    // Delay opening the dialog slightly to allow the dropdown menu to close
    // and cleanup its focus/pointer-events state. This prevents the UI from
    // becoming unresponsive if the user cancels the dialog.
    setTimeout(() => {
      setAssignDialogOpen(true);
    }, 100);
  };

  return (
    <>
      <div className='fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50'>
        <div className='bg-primary text-primary-foreground px-2 py-2 rounded-lg shadow-lg flex items-center gap-2'>
          <div className='flex items-center gap-2 h-7 px-2 bg-black/20 rounded border border-white/10'>
            <span className='text-sm'>{selectedIntakeIds.length} selected</span>
            <Button
              onClick={onClearSelection}
              size='icon'
              variant='ghost'
              className='hover:bg-black/20'
              aria-label='Clear selection'
            >
              <X className='w-3.5 h-3.5' />
            </Button>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                className='h-7 px-2 text-sm bg-black/20 border border-white/10 rounded hover:bg-black/30 hover:text-primary-foreground'
              >
                Actions
                <ChevronDown className='w-3.5 h-3.5' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onClick={handleAssignClick}>
                <UserPlus className='w-4 h-4 mr-2' />
                Assign
              </DropdownMenuItem>
              {canArchive && (
                <DropdownMenuItem
                  onClick={() => handleBulkActionClick('archive')}
                >
                  <Archive className='w-4 h-4 mr-2 text-orange-600' />
                  Archive
                </DropdownMenuItem>
              )}
              {canUnarchive && (
                <DropdownMenuItem
                  onClick={() => handleBulkActionClick('unarchive')}
                >
                  <ArchiveRestore className='w-4 h-4 mr-2 text-green-600' />
                  Unarchive
                </DropdownMenuItem>
              )}
              {canNonAdmit && (
                <DropdownMenuItem
                  onClick={() => handleBulkActionClick('non_admit')}
                >
                  <Ban className='w-4 h-4 mr-2 text-red-600' />
                  Mark Non-Admit
                </DropdownMenuItem>
              )}
              {canUndoNonAdmit && (
                <DropdownMenuItem
                  onClick={() => handleBulkActionClick('undo_non_admit')}
                >
                  <RotateCcw className='w-4 h-4 mr-2 text-green-600' />
                  Undo Non-Admit
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <IntakeUserAssignmentDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        intakes={selectedIntakes}
        onSuccess={() => {
          onClearSelection();
          onAssignComplete?.();
        }}
      />

      <Dialog
        open={actionDialogOpen}
        onOpenChange={open => {
          if (!open) {
            setActionDialogOpen(false);
            setBulkActionType(null);
          }
        }}
      >
        <DialogContent
          onCloseAutoFocus={e => e.preventDefault()}
          onOpenAutoFocus={e => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              {bulkActionType === 'archive' &&
                `Archive ${selectedIntakeIds.length === 1 ? 'Intake' : 'Intakes'}`}
              {bulkActionType === 'unarchive' &&
                `Unarchive ${selectedIntakeIds.length === 1 ? 'Intake' : 'Intakes'}`}
              {bulkActionType === 'non_admit' &&
                `Mark ${selectedIntakeIds.length === 1 ? 'Intake' : 'Intakes'} as Non-Admit`}
              {bulkActionType === 'undo_non_admit' &&
                `Undo Non-Admit for ${selectedIntakeIds.length === 1 ? 'Intake' : 'Intakes'}`}
            </DialogTitle>
          </DialogHeader>
          <div className='py-4'>
            <p className='text-muted-foreground'>
              {bulkActionType === 'archive' &&
                `Are you sure you want to archive ${selectedIntakeIds.length === 1 ? 'this intake' : `${selectedIntakeIds.length} selected intakes`}?`}
              {bulkActionType === 'unarchive' &&
                `Are you sure you want to unarchive ${selectedIntakeIds.length === 1 ? 'this intake' : `${selectedIntakeIds.length} selected intakes`}?`}
              {bulkActionType === 'non_admit' &&
                `Are you sure you want to mark ${selectedIntakeIds.length === 1 ? 'this intake' : `${selectedIntakeIds.length} selected intakes`} as non-admit?`}
              {bulkActionType === 'undo_non_admit' &&
                `Are you sure you want to undo non-admit for ${selectedIntakeIds.length === 1 ? 'this intake' : `${selectedIntakeIds.length} selected intakes`}?`}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setActionDialogOpen(false);
                setBulkActionType(null);
              }}
              disabled={isBulkActionLoading}
            >
              No
            </Button>
            <Button
              onClick={handleBulkActionConfirm}
              disabled={isBulkActionLoading}
            >
              {isBulkActionLoading && (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              )}
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default IntakeSelectionActions;
